import React, { useEffect, useRef, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import MessagingList from "./messagingList";
import SunEditor from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";
import { useDispatch, useSelector } from "react-redux";

import {
  getCurrentUser,
  getUserProfilePic,
  getUserProfilePicTwo,
  loadUserProfilePicTwo,
} from "../../app/store/auth";

import {
  addAppMessage,
  getAddedNewMessageStatus,
  getAppMsgList,
  getAppMsgReadStatus,
  loadAppMsgList,
  updateMessageRead,
  updateMsgListRead,
} from "../../app/store/messages";

import {
  getComponentUserStatus,
  UpdateComponentsUserStatus,
} from "../../app/store/tools";

const MessagingPopup = ({
  msgShowHide,
  handleMsgModalShowHide,
  message,
  handleSendMessage,
  handleSupportMessage,
}) => {
  const dispatch = useDispatch();
  const currentUser = getCurrentUser();

  const [messageList, setMessageList] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [content, setContent] = useState("");
  const [profilePicData, setProfilePicData] = useState(null);
  const [profilePicTwoData, setProfilePicTwoData] = useState(null);

  const fileInputRef = useRef(null);

  // Redux Selectors
  const appMsgListSelector = useSelector(getAppMsgList);
  const addedNewMessageStatusSelector = useSelector(getAddedNewMessageStatus);
  const componentUserStatusSelector = useSelector(getComponentUserStatus);
  const appMsgReadStatusSelector = useSelector(getAppMsgReadStatus);
  const userProfilePicSelector = useSelector(getUserProfilePic);
  const userProfilePicSelectorTwo = useSelector(getUserProfilePicTwo);

  const toolbar = [
    [
      "undo",
      "redo",
      "font",
      "fontSize",
      "formatBlock",
      "bold",
      "underline",
      "italic",
      "fontColor",
      "hiliteColor",
      "removeFormat",
      "link",
      "image",
    ],
  ];

  // ------------------------------------------------------------
  // Load message thread when modal opens or message.id changes
  // ------------------------------------------------------------
  useEffect(() => {
    if (message?.id) {
      dispatch(loadAppMsgList(message.id));
    }
    setDocuments([]);
    setContent("");
    setMessageList([]);
  }, [message, dispatch]);

  // ------------------------------------------------------------
  // Build message list from Redux store
  // ------------------------------------------------------------
  useEffect(() => {
    if (appMsgListSelector.length === 0) return;

    const msgList = appMsgListSelector.map((item) => {
      const isSender = item.fromId === parseInt(currentUser.id);
      const performer = `${item.fromName} ${item.fromLastName}`;

      if (!isSender) {
        dispatch(loadUserProfilePicTwo(item.fromId));
      }

      return {
        author: isSender ? "send" : "receive",
        data: item.message,
        performer,
        date: item.messageDate,
        documents: item.documents,
        id: item.id,
        readStatus: item.readStatus,
      };
    });

    setMessageList(msgList);

    // Mark unread threads as read
    const unread = msgList.some((x) => x.readStatus !== true);

    if (unread && message?.id) {
      dispatch(updateMessageRead(message.id, currentUser.id));
    }

  }, [appMsgListSelector]);

  // ------------------------------------------------------------
  // Load current user's picture
  // ------------------------------------------------------------
  useEffect(() => {
    if (userProfilePicSelector) {
      setProfilePicData("data:image/jpeg;base64," + userProfilePicSelector);
    }
  }, [userProfilePicSelector]);

  // ------------------------------------------------------------
  // Load other party's picture
  // ------------------------------------------------------------
  useEffect(() => {
    if (userProfilePicSelectorTwo) {
      setProfilePicTwoData("data:image/jpeg;base64," + userProfilePicSelectorTwo);
    }
  }, [userProfilePicSelectorTwo]);

  // ------------------------------------------------------------
  // Handle read-status acknowledgement
  // ------------------------------------------------------------
  useEffect(() => {
    if (!appMsgReadStatusSelector) return;

    const msgItems = messageList.map((m) => m.id).join(",");
    if (!msgItems) return;

    handleUpdateComponent(msgItems);
    updateMsgListRead(false);

    if (handleSendMessage) handleSendMessage();
  }, [appMsgReadStatusSelector, messageList]);

  // ------------------------------------------------------------
  // Update component user status tracking
  // ------------------------------------------------------------
  const handleUpdateComponent = (compItems) => {
    const existing = componentUserStatusSelector.find((x) => x.compId === 9);
    const previousItems = existing?.compItems || "";

    const compObj = {
      compName: "Messages",
      performerId: currentUser.id,
      compItems,
      ciid: message.ciid,
      wiid: message.wiid,
      iiid: message.iiid,
      compId: 9,
    };

    if (compItems !== "" && compItems !== previousItems) {
      dispatch(UpdateComponentsUserStatus(compObj));
    }
  };

  // ------------------------------------------------------------
  // Editor Change
  // ------------------------------------------------------------
  const handleChange = (newContent) => setContent(newContent);

  // ------------------------------------------------------------
  // File Upload
  // ------------------------------------------------------------
  const onFileChange = (event) => {
    const newFiles = Array.from(event.target.files).map((file) => ({
      name: file.name,
      type: file.type,
      size: (file.size / 1024).toFixed(2),
      file,
    }));

    setDocuments((prev) => [...prev, ...newFiles]);
  };

  // ------------------------------------------------------------
  // Remove File
  // ------------------------------------------------------------
  const handleFileRemove = (item) => {
    setDocuments((prev) => prev.filter((doc) => doc !== item));
  };

  // ------------------------------------------------------------
  // Submit New Message
  // ------------------------------------------------------------
  const onSubmit = () => {
    const data = new FormData();
    data.append("message", content);
    data.append("performerId", currentUser.id);
    data.append("communication", JSON.stringify(message));
    data.append("upDocs", "");

    if (documents.length > 0) {
      data.append("upDocs", JSON.stringify(documents));
      documents.forEach((doc, idx) =>
        data.append(`files[${idx}]`, doc.file)
      );
    }

    dispatch(addAppMessage(data));
  };

  // ------------------------------------------------------------
  // After new message successfully added
  // ------------------------------------------------------------
  useEffect(() => {
    if (!addedNewMessageStatusSelector?.state) return;

    dispatch(loadAppMsgList(message.id));

    if (handleSupportMessage) {
      handleSupportMessage(content, addedNewMessageStatusSelector.obj);
    }

    setDocuments([]);
    setContent("");
    setMessageList([]);
  }, [addedNewMessageStatusSelector]);

  return (
    <Modal show={msgShowHide} size="xl">
      <Modal.Header closeButton onHide={() => handleMsgModalShowHide("")}>
        <Modal.Title>Messages</Modal.Title>
      </Modal.Header>

      <Modal.Body className="border">
        {/* MESSAGES AREA */}
        <div
          className="row mt--3"
          style={{
            maxHeight: 350,
            overflow: "auto",
            display: "flex",
            flexDirection: "column-reverse",
          }}
        >
          <div className="col-md-12 bg-white">
            {messageList.map((item) => (
              <MessagingList
                key={item.id}
                message={item}
                profilePicData={profilePicData}
                profilePicTwoData={profilePicTwoData}
              />
            ))}
          </div>
        </div>

        {/* FILE PREVIEW */}
        {documents.length > 0 && (
          <div className="file-preview">
            {documents.map((file, index) => (
              <div key={index} className="file-preview-item ml-2">
                <span>{file.name}</span>
                <span
                  className="file-size"
                  style={{
                    backgroundColor: "#f0f0f0",
                    marginLeft: "10px",
                    padding: "2px 5px",
                    borderRadius: "5px",
                  }}
                >
                  {file.size} KB
                </span>
                <button
                  className="btn btn-sm btn-link"
                  onClick={() => handleFileRemove(file)}
                >
                  <i className="fa fa-times text-red" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* EDITOR */}
        <div className="col-md-12 mt-2">
          <SunEditor
            setOptions={{
              height: 50,
              buttonList: toolbar,
            }}
            onChange={handleChange}
            setContents={content}
          />
        </div>

        {/* ACTION BUTTONS */}
        <div className="col-12 text-center mt-3 d-flex justify-content-center">
          <Button
            variant="outline-dark"
            size="sm"
            onClick={() => fileInputRef.current.click()}
          >
            <i className="fa fa-paperclip mr-2"></i>Add
          </Button>

          <Button variant="outline-dark" size="sm" onClick={onSubmit}>
            <i className="fa fa-paper-plane-o mr-2"></i>Send
          </Button>
        </div>

        <input
          type="file"
          ref={fileInputRef}
          onChange={onFileChange}
          multiple
          style={{ position: "absolute", top: "-9999px" }}
        />
      </Modal.Body>
    </Modal>
  );
};

export default MessagingPopup;
